import { FC } from 'react';
import type { SeopBands } from '@/types/seop';
import { BANDAS_PADRAO, barTone, fmtUtil, seopStatus, toneTextClass } from '@/utils/seopFormat';

interface UtilizationBarProps {
  util: number;
  /** Faixas em vigor. Ausente = o padrão, para a barra não depender de quem a usa. */
  bands?: SeopBands;
}

const FILL_CLASSES = {
  danger: 'bg-theme-danger',
  warning: 'bg-theme-warning',
  base: 'bg-accent',
} as const;

/**
 * Utilização de um recurso: mini-barra + percentual.
 *
 * As duas regras de cor do original convivem aqui, e **divergem de propósito**: o
 * preenchimento da barra usa a regra grossa (`barTone`, em que folga fica na cor
 * padrão, não verde) e o texto usa a regra do semáforo (`seopStatus`, em que
 * Balanceado é âmbar e Folga é verde). Não unificar — o Herrison reconhece a tela
 * dele pela cor.
 *
 * A escala satura em 160% (largura `pct/1.6`), como no app: acima disso a barra
 * para de crescer em vez de estourar a célula.
 */
const UtilizationBar: FC<UtilizationBarProps> = ({ util, bands = BANDAS_PADRAO }) => {
  const pct = Number.isFinite(util) ? Math.min(util * 100, 160) : 160;
  const tone = barTone(util, bands);
  const fill = tone ? FILL_CLASSES[tone] : FILL_CLASSES.base;

  return (
    <div className="flex items-center justify-end gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-tertiary">
        <div className={`h-full rounded-full ${fill}`} style={{ width: `${pct / 1.6}%` }} role="presentation" />
      </div>
      <span
        className={`w-14 text-right text-sm font-semibold tabular-nums ${toneTextClass(seopStatus(util, bands).tone)}`}
      >
        {fmtUtil(util)}
      </span>
    </div>
  );
};

export default UtilizationBar;
